import { DealStatus } from '@/app/api/types';
import { Chip, ChipProps, SxProps } from '@mui/material';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  status: DealStatus;
  type: 'buyer' | 'seller' | 'factor';
  label?: string;
}

export const DealStatusChip: FC<Props> = ({ status, type, label }) => {
  const { t } = useTranslation();

  const getChipSx = (): SxProps => {
    let bgcolor = 'transparent';
    let color = 'text.primary';
    let borderColor = 'transparent';

    switch (status) {
      case 'underReview':
      case 'active':
        bgcolor = 'rgba(21, 85, 252, 0.1)';
        color = 'primary.main';
        break;
      case 'waitingForBuyerAcceptance':
        color = 'primary.main';
        borderColor = 'primary.main';
        break;
      case 'waitingForAcceptance':
      case 'waitingForClarification':
      case 'waitingForFactorAcceptance':
      case 'contractCreated':
      case 'waitContragentSign':
        bgcolor = 'secondary.main';
        break;
      case 'contractCreatedByFactor':
        bgcolor = 'rgba(11, 19, 112, 0.1)';
        color = 'success.main';
        break;
      case 'deleted':
      case 'declined':
      case 'contractRejected':
        color = 'rgba(236, 83, 17, 1)';
        bgcolor = 'rgba(236, 83, 17, 0.1)';
        break;
      case 'expired':
        color = 'rgba(102, 102, 102, 1)';
        bgcolor = 'rgba(102, 102, 102, .1)';
        break;
      case 'draft':
        borderColor = 'primary.light';
        break;
      case 'completed':
        bgcolor = '#007BFF';
        color = 'white';
        break;
      case 'paid':
        bgcolor = '#28A745';
        color = 'white';
        break;
      case 'redeemed':
        bgcolor = '#218838';
        color = 'white';
        break;
      case 'overdue':
        bgcolor = '#DC3545';
        color = 'white';
        break;
      case 'partiallyPaid':
        bgcolor = '#A2D39C';
        color = 'white';
        break;
      case 'partiallyRedeemed':
        bgcolor = '#FD7E14';
        color = 'white';
        break;
      default:
        break;
    }

    return {
      bgcolor,
      color,
      borderColor,
      fontWeight: 500,
      fontSize: 10,
      height: 20,
      px: 1,
      '& .MuiChip-label': {
        px: 0,
      },
    };
  };

  const getVariant = (): ChipProps['variant'] => {
    let variant: ChipProps['variant'] = 'filled';

    switch (status) {
      case 'waitingForBuyerAcceptance':
      case 'draft':
        variant = 'outlined';
        break;
      default:
        break;
    }

    return variant;
  };

  return (
    <Chip
      variant={getVariant()}
      sx={getChipSx()}
      label={label || t(`statuses.${type}.${status}`)}
    />
  );
};
